import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, ExternalLink, Loader2, Hourglass, Home } from "lucide-react";
import { api } from "../lib/api";
import Layout from "../components/Layout";

// Page tampon utilisée après le clic "Payer sur PayPal".
// 1) Ouvre automatiquement le lien PayPal dans un nouvel onglet.
// 2) Propose au donateur un bouton "J'ai finalisé mon paiement" qui met le don
//    en "awaiting_review". Le reçu fiscal + email de remerciement est envoyé
//    PLUS TARD, après que l'admin ait vérifié la réception dans son compte PayPal.
export default function PaypalReturn() {
  const [params] = useSearchParams();
  const donationId = params.get("id");
  const token = params.get("token");
  const paypalUrl = params.get("url");
  const amount = params.get("amount");
  const program = params.get("program");
  const [state, setState] = useState({ status: "idle", error: null });
  const [opened, setOpened] = useState(false);

  useEffect(() => {
    if (!paypalUrl || opened) return;
    const win = window.open(paypalUrl, "_blank", "noopener,noreferrer");
    setOpened(true);
    if (win) win.focus?.();
  }, [paypalUrl, opened]);

  const confirmPayment = async () => {
    if (!donationId || !token) { setState({ status: "error", error: "Lien invalide" }); return; }
    setState({ status: "pending", error: null });
    try {
      await api.post("/donations/paypal-confirm", { id: donationId, token });
      setState({ status: "done", error: null });
    } catch (err) {
      setState({ status: "error", error: err?.response?.data?.detail || err?.message || "Erreur" });
    }
  };

  return (
    <Layout>
      <div className="min-h-[70vh] bg-ivory grid place-items-center py-20 px-5" data-testid="paypal-return-page">
        <div className="max-w-2xl w-full">
          {state.status !== "done" && (
            <div className="p-8 rounded-2xl bg-white shadow-lg text-center">
              <div className="w-16 h-16 rounded-full bg-warm-orange grid place-items-center mx-auto shadow">
                <ExternalLink className="text-ivory" size={28}/>
              </div>
              <h1 className="font-display text-3xl sm:text-4xl text-deep-blue mt-6" data-testid="paypal-title">Finalisez votre don PayPal</h1>
              {amount && (
                <p className="text-deep-blue/70 mt-3">Montant : <b>{amount} €</b>{program && <> pour <i>{program}</i></>}</p>
              )}
              <div className="mt-6 text-sm text-deep-blue/80 space-y-3">
                <p><b>Étape 1</b> — Un nouvel onglet PayPal s'est ouvert
                  {paypalUrl && (<> (<a href={paypalUrl} target="_blank" rel="noopener noreferrer" className="text-warm-orange underline" data-testid="paypal-fallback">rouvrir le lien</a> si besoin)</>)}
                  . Effectuez votre paiement.
                </p>
                <p><b>Étape 2</b> — Une fois le paiement effectué sur PayPal, revenez ici et cliquez ci-dessous pour nous le signaler.</p>
              </div>
              <button onClick={confirmPayment} disabled={state.status === "pending"}
                className="btn-gold mt-8 px-8 py-4 rounded-full font-semibold inline-flex items-center gap-2 disabled:opacity-60"
                data-testid="paypal-confirm-btn">
                {state.status === "pending"
                  ? <><Loader2 className="animate-spin" size={18}/> Envoi en cours…</>
                  : <><CheckCircle2 size={18}/> J'ai finalisé mon paiement PayPal</>}
              </button>
              {state.status === "error" && (
                <p className="text-warm-orange mt-4 text-sm" data-testid="paypal-error">{state.error}</p>
              )}
            </div>
          )}
          {state.status === "done" && (
            <div className="p-8 rounded-2xl bg-white shadow-lg text-center" data-testid="paypal-success">
              <div className="w-16 h-16 rounded-full bg-warm-orange grid place-items-center mx-auto shadow">
                <Hourglass className="text-ivory" size={28}/>
              </div>
              <h1 className="font-display text-3xl sm:text-4xl text-deep-blue mt-6">Merci de votre geste 🙏</h1>
              <p className="text-deep-blue/80 mt-4 leading-relaxed">
                Nous avons bien noté votre paiement. Notre équipe va <b>vérifier la réception du don</b> sur notre compte PayPal (généralement sous <b>24 à 48 h</b>).
              </p>
              <p className="text-deep-blue/80 mt-3 leading-relaxed">
                Dès confirmation, vous recevrez par email votre <b>message de remerciement personnalisé</b> accompagné de votre <b>reçu fiscal officiel</b> au format PDF.
              </p>
              <div className="mt-6 p-4 rounded-xl bg-gold/10 border border-gold/30 text-sm text-deep-blue/70">
                Un seul email vous sera envoyé — après validation du paiement — pour éviter tout doublon.
              </div>
              <Link to="/" className="btn-gold mt-8 px-6 py-3 rounded-full font-semibold inline-flex items-center gap-2" data-testid="paypal-home">
                <Home size={16}/> Retour à l'accueil
              </Link>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
